import { useCallback, useEffect, type FC } from "react";

import { nodeTitle } from "@/features/caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import { CitationHoverCard } from "../../draftCitations/CitationHoverCard";
import {
  citationLabel,
  nodeById,
  type CitationRef
} from "../../draftCitations/model";
import { useCitationChrome } from "../../draftCitations/useCitationChrome";
import type { DocxSurfaceHandle } from "../../draftEditor/surface";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";

type Props = {
  /** The element wrapping the docx editor; null until it mounts. */
  host: HTMLElement | null;
  /** The mounted editor surface; null until it is up. Decides how citations are located. */
  surface?: DocxSurfaceHandle | null;
  /** Reports the node ids the open document cites, as they are painted. */
  onCitedNodesChange?: (nodeIds: string[]) => void;
};

/**
 * Makes the citations in an open draft live: hovering one (or landing the caret
 * in it) shows the exhibit's current label, file, and cited page, with a way to
 * open the file there. Labels come from the filesystem the IDE already holds,
 * so the card is right even before the document repaints after a renumbering.
 */
export const DraftCitationLayer: FC<Props> = ({
  host,
  surface = null,
  onCitedNodesChange
}) => {
  const { individualId, open } = useCaseIde();
  const { fs } = useCaseData();

  const openExhibit = useCallback(
    (node: FsNode, page: number | null) => {
      open({ kind: "file", id: node.id, title: nodeTitle(node), page });
    },
    [open]
  );

  const activate = useCallback(
    (ref: CitationRef) => {
      const node = nodeById(fs.nodes, ref.nodeId);
      if (node) openExhibit(node, ref.page);
    },
    [fs.nodes, openExhibit]
  );

  const chrome = useCitationChrome(host, { surface, onActivate: activate });

  useEffect(() => {
    onCitedNodesChange?.(chrome.citedNodeIds);
  }, [chrome.citedNodeIds, onCitedNodesChange]);

  const { hit } = chrome;
  const node = hit ? nodeById(fs.nodes, hit.nodeId) : null;
  const label = hit ? citationLabel(fs.labels, fs.scheme, hit) : null;

  return (
    <CitationHoverCard
      anchor={hit?.rect ?? null}
      individualId={individualId}
      nodeId={hit?.nodeId ?? null}
      page={hit?.page ?? null}
      node={node}
      label={label}
      onHold={chrome.hold}
      onDismiss={chrome.dismiss}
      onOpen={(target, page) => {
        chrome.dismiss();
        openExhibit(target, page);
      }}
    />
  );
};
